import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

/**
 * SONMI · Atrium — the front door.
 *
 * Where you are before you are anywhere. Answers "where am I?"
 * before it answers "what should I do?" No dashboard, no
 * recommendations, no urgency. Doors are named. The silence between
 * them is part of the design.
 *
 * See /app/memory/ECOLOGY_OF_UNDERSTANDING.md.
 */

function greetingForNow() {
  const h = new Date().getHours();
  if (h < 5) return "Good evening";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

export default function Atrium() {
  const [greeting, setGreeting] = useState(greetingForNow());

  // Refresh the greeting if the tab is left open across a threshold.
  useEffect(() => {
    const t = setInterval(() => setGreeting(greetingForNow()), 60_000);
    return () => clearInterval(t);
  }, []);

  return (
    <div
      className="min-h-screen w-full"
      style={{ backgroundColor: "#FAFAF7", fontFamily: "'Outfit', sans-serif", color: "#44403c" }}
      data-testid="atrium-page"
    >
      {/* Corner stamp — matches the study pages and the Observatory. */}
      <div
        className="absolute top-6 left-8 text-[10px] uppercase tracking-[0.18em] text-stone-400"
        data-testid="atrium-stamp"
      >
        SONMI · Atrium
      </div>

      <div className="max-w-2xl mx-auto px-8 pt-40 pb-32">
        {/* Greeting — not about work. */}
        <p
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 300,
            fontSize: "2rem",
            lineHeight: 1.3,
            letterSpacing: "-0.005em",
            color: "#292524",
          }}
          data-testid="atrium-greeting"
        >
          {greeting}, Damon.
        </p>
        <p
          className="mt-3 italic text-stone-500"
          style={{ fontSize: "1.05rem", lineHeight: 1.55 }}
          data-testid="atrium-invitation"
        >
          Take what's useful. Leave what's not.
        </p>

        {/* A generous silence before the wordmark. */}
        <div className="h-40" />

        <h1
          className="text-stone-900"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 300,
            fontSize: "3rem",
            letterSpacing: "0.02em",
            lineHeight: 1,
          }}
          data-testid="atrium-wordmark"
        >
          SONMI
        </h1>
        <p
          className="mt-5 italic text-stone-500"
          style={{ fontSize: "1.05rem", lineHeight: 1.6, maxWidth: "34rem" }}
          data-testid="atrium-tagline"
        >
          Where understanding is allowed to remain unfinished without being lost.
        </p>

        <Divider className="mt-16" />

        <nav className="mt-16 space-y-14" data-testid="atrium-doors">
          <Door
            symbol="◉"
            title="The Observatory"
            testId="door-observatory"
            links={[
              { label: "Record a First Arrival", to: "/sonmi/first-arrival" },
            ]}
          />

          <Door
            symbol="◉"
            title="The Written Record"
            testId="door-record"
            links={[
              { label: "The Ecology of Understanding", to: "/sonmi/ecology" },
              { label: "Frozen Studies", to: "/sonmi/studies" },
            ]}
          />

          <Door
            symbol="◉"
            title="Today's Work"
            testId="door-work"
            links={[
              { label: "Reviews", to: "/reviews/yext" },
              { label: "Rainbow", to: "/home" },
            ]}
          />
        </nav>

        <Divider className="mt-24" faint />

        <div className="mt-8" data-testid="atrium-legacy">
          <Link
            to="/home"
            className="text-[11px] italic text-stone-400 hover:text-stone-700 transition-colors"
          >
            Legacy Workspace
          </Link>
        </div>
      </div>
    </div>
  );
}

function Divider({ className = "", faint = false }) {
  return (
    <div
      className={className}
      style={{
        height: 1,
        backgroundColor: faint ? "#f5f5f4" : "#e7e5e4",
      }}
    />
  );
}

function Door({ symbol, title, links, testId }) {
  return (
    <section data-testid={testId}>
      <div className="flex items-baseline gap-3">
        <span className="text-stone-300 text-sm leading-none">{symbol}</span>
        <h2
          className="text-stone-800"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 400,
            fontSize: "1.5rem",
            letterSpacing: "-0.005em",
            lineHeight: 1.1,
          }}
        >
          {title}
        </h2>
      </div>
      <ul className="mt-3 ml-6 space-y-2">
        {links.map((l) => (
          <li key={l.to}>
            <Link
              to={l.to}
              className="text-stone-600 hover:text-stone-900 transition-colors"
              style={{ fontSize: "1rem", lineHeight: 1.65 }}
              data-testid={`door-link-${l.to.replace(/[^a-z0-9]+/gi, "-")}`}
            >
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
